import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import {
  Order,
  Response,
  CreateUgl,
  CreateBrand,
  Branding,
} from "../interfaces";
import { CreateTextReminderDto, DashboardRoot } from "./orderTypes";

export const orderApi = createApi({
  reducerPath: "orderApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["order", "ugl", "branding"],
  endpoints: (builder) => ({
    createViaEmail: builder.mutation<Response<Order>, any>({
      query: (credentials) => ({
        url: "order/createViaEmail",
        method: "POST",
        data: credentials,
      }),
    }),
    orderStats: builder.query<Response<DashboardRoot>, any>({
      query: (credentials) => ({
        url: "order/stats",
        method: "GET",
        data: credentials,
      }),
    }),
    deleteOrder: builder.mutation<Response<Order>, { orderHash: string }>({
      query: (credentials) => ({
        url: "order/" + credentials.orderHash,
        method: "DELETE",
        data: credentials,
      }),
      invalidatesTags: ["order"],
    }),
    updateOrder: builder.mutation<
      Response<Order>,
      { order_hash: string; handwritten_note: string }
    >({
      query: (credentials) => ({
        url: "order",
        method: "PATCH",
        data: credentials,
      }),
      invalidatesTags: ["order"],
    }),
    createViaLink: builder.mutation<Response<Order[]>, any>({
      query: (body) => ({
        url: "order/createViaLink",
        method: "POST",
        data: body,
      }),
    }),
    orderReminderEmail: builder.mutation<Response<Order[]>, any>({
      query: (credentials) => ({
        url: "reminder/sendOrderEmail",
        method: "POST",
        data: credentials,
      }),
    }),
    orderReminderText: builder.mutation<
      Response<Order[]>,
      CreateTextReminderDto
    >({
      query: (body) => ({
        url: "reminder/sendOrderText",
        method: "POST",
        data: body,
      }),
    }),
    orderList: builder.query<
      Response<{ orders: Order[]; total_count: number }>,
      any
    >({
      query: (params) => ({
        url: "order/list",
        method: "GET",
        params,
      }),
      providesTags: ["order"],
    }),
    orderListExport: builder.query<
      Response<{ orders: Order[]; total_count: number }>,
      any
    >({
      query: (params) => ({
        url: "order/list-export",
        method: "GET",
        params,
      }),
    }),
    trackingFilterOptions: builder.query<
      Response<{ senders: { email: string; name: string }[]; send_via: string[] }>,
      void
    >({
      query: () => ({
        url: "order/list-filters",
        method: "GET",
      }),
      providesTags: ["order"],
    }),
    createUgl: builder.mutation<Response<CreateUgl[]>, any>({
      query: (body) => ({
        url: "ugl/create",
        method: "POST",
        data: body,
      }),
      invalidatesTags: ["ugl"],
    }),
    getUglList: builder.query<Response<CreateUgl[]>, any>({
      query: (params) => ({
        url: "ugl/list",
        method: "GET",
        params,
      }),
      providesTags: ["ugl"],
    }),
    updateUgl: builder.mutation<Response<CreateUgl[]>, any>({
      query: ({ id, ...rest }) => ({
        url: "ugl/" + id,
        method: "PATCH",
        data: rest,
      }),
      invalidatesTags: ["ugl"],
    }),
    createBrand: builder.mutation<Response<CreateBrand[]>, any>({
      query: (body) => ({
        url: "branding/create",
        method: "POST",
        data: body,
        headers: { "Content-Type": "multipart/form-data" },
      }),
      invalidatesTags: ["branding"],
    }),
    getBrandingList: builder.query<Response<Branding[]>, any>({
      query: (params) => ({
        url: "branding/list",
        method: "GET",
        params,
      }),
      providesTags: ["branding"],
    }),
  }),
});

export const {
  useOrderStatsQuery,
  useDeleteOrderMutation,
  useCreateViaEmailMutation,
  useUpdateOrderMutation,
  useCreateViaLinkMutation,
  useOrderReminderEmailMutation,
  useOrderReminderTextMutation,
  useOrderListQuery,
  useLazyOrderListExportQuery,
  useTrackingFilterOptionsQuery,
  useCreateUglMutation,
  useGetUglListQuery,
  useUpdateUglMutation,
  useCreateBrandMutation,
  useGetBrandingListQuery,
} = orderApi;
